import { AuthMachineContext } from './authTransitions';

/**
 * Retry classification for the profile fetch.
 * Retry on 5xx (backend starting up) or network errors (connection refused).
 * AbortError (self-imposed 10s timeout) is NOT retried — if the backend can't
 * respond within 10s it's unlikely to recover on immediate retry.
 */
export function isRetryableError(status: number | undefined, error?: unknown): boolean {
  if (status && status >= 500) return true;
  if (error instanceof TypeError && error.message.includes('fetch')) return true;
  return false;
}

/**
 * Collaborators the profile fetch needs from the owning state machine. The
 * AbortController and retry-delay timer are owned by the machine and surfaced
 * here as explicit set callbacks so cancellation in `destroy()` keeps working
 * exactly as before — the fetch never owns those handles in isolation.
 */
export interface ProfileFetchDeps {
  context: AuthMachineContext;
  isDestroyed: () => boolean;
  setActiveAbortController: (controller: AbortController | null) => void;
  setRetryDelayTimer: (timer: ReturnType<typeof setTimeout> | null) => void;
  log: (message: string) => void;
  error: (message: string, error?: unknown) => void;
}

/**
 * Fetch the backend user profile with a 5-attempt retry (3s delay between
 * attempts), a 10s per-attempt abort timeout, and mid-retry session re-checks.
 * Behavior is identical to the previous in-class method; only the abort/timer
 * handles are now threaded through `deps` so the machine retains ownership.
 */
export async function fetchUserProfile(userId: string, deps: ProfileFetchDeps): Promise<boolean> {
  const { context, isDestroyed, setActiveAbortController, setRetryDelayTimer, log, error } = deps;

  if (!context.session?.access_token) {
    error('No access token available for profile fetch');
    return false;
  }

  const apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/auth/user-info`;
  const maxRetries = 5;
  const retryDelayMs = 3000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    if (isDestroyed()) return false;

    // Re-check session each attempt — it can be cleared by SIGNED_OUT mid-retry
    if (!context.session?.access_token) {
      error('Session lost during profile fetch retry');
      return false;
    }

    const controller = new AbortController();
    setActiveAbortController(controller);
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      log(`Fetching profile (attempt ${attempt}/${maxRetries}) from: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${context.session.access_token}`,
        },
        signal: controller.signal,
      });

      if (response.ok) {
        const backendProfile = await response.json();
        log(`Profile fetch successful for user: ${backendProfile.email}`);

        context.profile = {
          id: userId,
          email: backendProfile.email,
          full_name: backendProfile.name,
          created_at:
            backendProfile.created_at || context.user?.created_at || new Date().toISOString(),
          organization_id: backendProfile.organization_id,
          organization: backendProfile.organization,
          is_global_admin: backendProfile.is_global_admin,
        };

        return true;
      }

      // Non-retryable HTTP error (401, 403, 404, etc.) — fail immediately
      if (!isRetryableError(response.status)) {
        error(`Profile fetch failed with status: ${response.status} ${response.statusText}`);
        try {
          const errorText = await response.text();
          error(`Response body: ${errorText}`);
        } catch (_bodyError) {
          error('Could not read error response body');
        }
        return false;
      }

      // Retryable HTTP error (5xx)
      error(`Profile fetch returned ${response.status} (attempt ${attempt}/${maxRetries})`);
    } catch (err) {
      // Destroyed while fetching — exit silently
      if (isDestroyed()) return false;

      // Network error or timeout
      if (!isRetryableError(undefined, err)) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          error(`Profile fetch timed out (attempt ${attempt}/${maxRetries})`);
        } else {
          error('Profile fetch failed with non-retryable error', err);
        }
        return false;
      }

      error(`Network error during profile fetch (attempt ${attempt}/${maxRetries})`, err);
    } finally {
      clearTimeout(timeoutId);
      setActiveAbortController(null);
    }

    // Wait before retrying (unless this was the last attempt)
    if (attempt < maxRetries) {
      await new Promise<void>((resolve) => {
        setRetryDelayTimer(
          setTimeout(() => {
            setRetryDelayTimer(null);
            resolve();
          }, retryDelayMs)
        );
      });
    }
  }

  error('Profile fetch failed after all retry attempts — backend may be unavailable');
  return false;
}
